import { useEffect, useState, useCallback } from "react";
import { ChevronDown, ChevronUp, Circle, ExternalLink, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Live-sektion för fotbollsmatcher som är registrerade på Min Fotboll.
 *
 * - Kompakt header alltid synlig: lag · ställning · live-prick · status-text
 * - Expanderbar (default kollapsad) för fullständig timeline med mål-händelser
 * - Pollar /minfotboll/status:
 *     - var 30 sek när expanderad
 *     - var 3 min när kollapsad (för att hålla ställningen uppdaterad)
 * - Visas bara om eventet har `minfotboll_game_id`
 */
export function FootballLiveSection({ eventId }) {
  const [state, setState] = useState(null); // { header, goals, ... }
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const r = await api.get(`/events/${eventId}/minfotboll/status`);
      setState(r.data);
    } catch (_e) {
      /* nät-fel — behåll tidigare state */
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [eventId]);

  useEffect(() => {
    load(true);
  }, [load]);

  // Auto-poll. Snabbare när expanderad (live-läget) långsammare när inte.
  useEffect(() => {
    if (!state?.registered) return;
    const intervalMs = expanded ? 30_000 : 180_000;
    const id = setInterval(() => load(true), intervalMs);
    return () => clearInterval(id);
  }, [expanded, load, state?.registered]);

  if (loading) return null;
  if (!state?.registered) return null;
  const h = state.header || {};
  const status = h.status_id;
  const isLive = status === 2;
  const isFinished = status === 3;
  const isNotStarted = status === 1 || !status;
  const goals = state.goals || [];

  const statusLabel = isLive ? "LIVE" : isFinished ? "SLUT" : "Ej startad";
  const statusColor = isLive
    ? "var(--danger, #DC2626)"
    : isFinished
    ? "var(--text-secondary)"
    : "var(--text-tertiary)";

  return (
    <section
      className="card-soft p-0 overflow-hidden border border-[color:var(--border-subtle)]"
      data-testid="football-live-section"
    >
      {/* Header (alltid synlig) */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-[color:var(--bg-elevated)] transition"
        data-testid="football-live-toggle"
      >
        <div className="flex items-center gap-1.5 shrink-0">
          {isLive && (
            <Circle
              size={8}
              fill="#DC2626"
              className="text-[#DC2626] animate-pulse"
            />
          )}
          <span
            className="text-[10px] font-bold tracking-wider"
            style={{ color: statusColor }}
          >
            {statusLabel}
          </span>
        </div>

        <div className="flex-1 min-w-0 text-left">
          <p className="text-xs font-semibold truncate">
            {h.home || "Hemmalag"}
            <span className="mx-2 tabular-nums">
              {h.home_score ?? "0"}–{h.away_score ?? "0"}
            </span>
            {h.away || "Bortalag"}
          </p>
          {h.league && (
            <p className="text-[10px] text-[color:var(--text-tertiary)] truncate">
              {h.league}
              {goals.length > 0 && ` · ${goals.length} mål`}
            </p>
          )}
        </div>

        {refreshing ? (
          <RefreshCw size={12} className="animate-spin text-[color:var(--text-tertiary)] shrink-0" />
        ) : expanded ? (
          <ChevronUp size={14} className="text-[color:var(--text-tertiary)] shrink-0" />
        ) : (
          <ChevronDown size={14} className="text-[color:var(--text-tertiary)] shrink-0" />
        )}
      </button>

      {/* Expanderat innehåll */}
      {expanded && (
        <div
          className="px-3 pb-3 pt-1 border-t border-[color:var(--border-subtle)] bg-[color:var(--bg-canvas)] max-h-[60vh] overflow-y-auto overscroll-contain"
          data-testid="football-live-expanded"
        >
          {/* Familjens spelare (Anton, William) - eget block om det finns barn */}
          {state.children && state.children.length > 0 && (
            <div className="mb-3 mt-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-1.5">
                Familjens spelare
              </p>
              <ul className="space-y-1">
                {state.children.map((c) => {
                  const rost = c.in_roster;
                  const events = c.events || [];
                  return (
                    <li
                      key={c.name}
                      className="flex items-start gap-2 text-xs"
                      data-testid={`football-child-${c.name.split(" ")[0]}`}
                    >
                      <span
                        className={`inline-flex items-center justify-center min-w-[1.75rem] h-5 px-1 rounded text-[10px] font-bold tabular-nums ${
                          rost
                            ? "bg-[color:var(--jarvis)] text-white"
                            : "bg-[color:var(--bg-elevated)] text-[color:var(--text-tertiary)]"
                        }`}
                      >
                        {rost?.shirt_number ?? "–"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium leading-tight">
                          {c.name}
                          {rost && (
                            <span className="ml-1.5 text-[10px] font-normal text-[color:var(--text-secondary)]">
                              ({rost.side === "home" ? h.home : h.away})
                            </span>
                          )}
                        </p>
                        {!rost && (
                          <p className="text-[10px] text-[color:var(--text-tertiary)] leading-tight">
                            Inte i publicerad trupp ännu
                          </p>
                        )}
                        {events.length > 0 ? (
                          <ul className="mt-1 space-y-0.5">
                            {events.map((e) => (
                              <li
                                key={e.id}
                                className="text-[11px] flex items-center gap-1.5"
                              >
                                {e.minute != null && (
                                  <span className="tabular-nums text-[color:var(--text-tertiary)]">
                                    {e.minute}′
                                  </span>
                                )}
                                <span>
                                  {e.kind === "goal" && "⚽ "}
                                  {e.kind === "yellow_card" && "🟨 "}
                                  {e.kind === "red_card" && "🟥 "}
                                  {e.kind === "substitution" && "🔁 "}
                                  {e.text}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : rost ? (
                          <p className="text-[10px] text-[color:var(--text-tertiary)] mt-0.5">
                            Inga händelser ännu
                          </p>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Alla mål i hela matchen */}
          {goals.length === 0 ? (
            <p className="text-[11px] text-[color:var(--text-tertiary)] py-2">
              {isNotStarted
                ? "Matchen har inte startat än. Vi pingar varje minut när det börjar närma sig avspark."
                : isFinished
                ? "Inga målhändelser registrerade."
                : "Inga mål ännu."}
            </p>
          ) : (
            <ul className="space-y-1.5 mt-1">
              {goals.map((g, i) => (
                <li
                  key={g.id || i}
                  className={`flex items-center gap-2 text-xs ${g.synthetic ? "opacity-50 italic" : ""}`}
                  data-testid={`football-goal-${i}`}
                  title={g.synthetic ? "Detta mål rapporterades inte live i Min Fotboll" : undefined}
                >
                  {g.minute != null && (
                    <span className={`inline-flex items-center justify-center min-w-[2.25rem] px-1.5 py-0.5 rounded text-white text-[10px] font-bold tabular-nums ${g.synthetic ? "bg-[color:var(--text-tertiary)]" : "bg-[color:var(--jarvis)]"}`}>
                      {g.synthetic ? "?" : `${g.minute}′`}
                    </span>
                  )}
                  <span className="flex-1 leading-snug">
                    {g.synthetic ? (
                      <span className="text-[color:var(--text-secondary)]">Mål ej rapporterat live</span>
                    ) : (
                      <>
                        <span className="font-medium">{g.scorer || "Mål"}</span>
                        {g.team && (
                          <span className="text-[color:var(--text-tertiary)]"> · {g.team}</span>
                        )}
                        {g.text && !g.scorer && (
                          <span className="text-[color:var(--text-secondary)]">{g.text}</span>
                        )}
                      </>
                    )}
                  </span>
                  {g.score_after && (
                    <span className="text-[10px] tabular-nums text-[color:var(--text-secondary)]">
                      {g.score_after}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-center gap-3 mt-2 pt-2 border-t border-[color:var(--border-subtle)]">
            <button
              type="button"
              onClick={() => load(false)}
              className="text-[10px] inline-flex items-center gap-1 text-[color:var(--text-secondary)] hover:text-[color:var(--jarvis)]"
              data-testid="football-refresh"
            >
              <RefreshCw size={10} /> Uppdatera nu
            </button>
            {state.game_id && (
              <a
                href={`https://minfotboll.svenskfotboll.se/Home/Public?target=magazinegameview&fullhash=%23%2Fmagazinegameview%2F${state.game_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] inline-flex items-center gap-1 text-[color:var(--text-secondary)] hover:text-[color:var(--jarvis)]"
              >
                <ExternalLink size={10} /> Öppna i Min Fotboll
              </a>
            )}
            {state.fetched_at && (
              <span className="text-[10px] text-[color:var(--text-tertiary)] ml-auto">
                Hämtat {new Date(state.fetched_at).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
